import {
  addressAutocompleteControllerSuggestQueryOptions,
  type AddressSuggestion,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import type { CountryCode } from 'libphonenumber-js/max';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getCountryLabel } from '../../../lib/country-options';
import { Autocomplete } from '../../ui/autocomplete';
import { useAddressSearchSessionId } from './address-search-session';

const MIN_QUERY_LENGTH = 3;

/** Search anchors. `country` is excluded: a closed set, picked locally. */
export type AddressPart = 'address' | 'postalCode' | 'city';

type AddressAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  label?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Leading adornment, forwarded so the field keeps its configured icon. */
  icon?: ReactNode;
  /** Which address part this field represents; drives what the selected suggestion writes here. */
  part?: AddressPart;
  /** Address group this field belongs to; its fields share one billed session. */
  addressGroupKey?: string;
  /** Sibling field names filled from the selected address (derived by the form-builder). */
  siblingFields?: {
    address?: string;
    postalCode?: string;
    city?: string;
    country?: string;
  };
  /** Restrict suggestions to these countries. Empty = worldwide (France ranked first). */
  countries?: CountryCode[];
};

/**
 * RHF-bound address field: owns the Mapbox suggest query (debounce + billed
 * session) and fills sibling fields on selection. The typeahead UI itself is the
 * generic `Autocomplete` primitive.
 */
export const AddressAutocomplete = ({
  value,
  onChange,
  onBlur,
  name,
  label,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  placeholder,
  disabled,
  icon,
  part = 'address',
  addressGroupKey,
  siblingFields,
  countries,
}: AddressAutocompleteProps) => {
  const form = useFormContext();
  const { i18n } = useTranslation();
  // One provider session for the whole address group: Mapbox bills a session, and
  // its own 180s idle rule ends ours (we never call /retrieve). Scoped by the
  // runtime name prefix so `assets.0.` and `assets.1.` stay separate sessions.
  const lastDot = name ? name.lastIndexOf('.') : -1;
  const namePrefix = lastDot >= 0 ? name!.slice(0, lastDot + 1) : '';
  const searchSessionId = useAddressSearchSessionId(
    addressGroupKey && `${namePrefix}${addressGroupKey}`,
  );
  // The value just applied by a selection — don't re-query it (avoids a wasted session).
  const selectedValueRef = useRef<string | null>(null);
  // The text the user last typed HERE, or null once they leave. State, not a ref:
  // blur has to re-render so `enabled` actually goes false, otherwise react-query
  // keeps an enabled query alive and refetches it on the next window focus.
  const [typedValue, setTypedValue] = useState<string | null>(null);

  const query = value ?? '';
  const debounced = useDebouncedValue(query, 500);
  // Only ever search text the user typed here, once the debounce has settled on it.
  // Anything else — a prefilled draft, a funnel remount, a sibling's cross-fill
  // landing mid-edit — leaves `query` different from what they typed.
  const shouldQuery =
    query === typedValue &&
    debounced === query &&
    query.trim().length >= MIN_QUERY_LENGTH &&
    query !== selectedValueRef.current;
  const { data, isFetching } = useQuery({
    ...addressAutocompleteControllerSuggestQueryOptions({
      q: debounced,
      searchSessionId,
      // Pass-through: the backend clamps to the provider's supported set.
      language: i18n.language,
      country: countries?.[0],
      // Scopes suggestions to this field's role (city → cities, country → countries).
      part,
    }),
    enabled: shouldQuery,
    // A typeahead result is only wanted while typing; never re-ask on a window
    // refocus or a reconnect, which would bill a session nobody asked for.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const suggestions = shouldQuery ? (data?.data ?? []) : [];

  // No /retrieve: the suggestion already carries the structured fields.
  const selectSuggestion = (suggestion: AddressSuggestion) => {
    // Derive from the code, not `suggestion.country`: the picker's options come from
    // `Intl.DisplayNames`, and a provider spelling it differently would not match one.
    const country = getCountryLabel(suggestion.countryCode, i18n.language) ?? suggestion.country;
    // The value that lands in THIS field depends on the part it represents.
    const ownValue = {
      address: suggestion.streetAddress || suggestion.label,
      postalCode: suggestion.postalCode,
      city: suggestion.locality,
    }[part];
    selectedValueRef.current = ownValue ?? '';
    onChange(ownValue ?? '');
    // Asset/customer funnels prefix field names (`assets.0.<key>`); mirror that
    // prefix so siblings resolve to the actual RHF fields, not top-level keys.
    const siblingName = (key: string) => `${namePrefix}${key}`;
    const setSibling = (key: string | undefined, fieldValue: string | undefined) => {
      if (key && fieldValue) {
        form?.setValue(siblingName(key), fieldValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    };
    setSibling(siblingFields?.address, suggestion.streetAddress);
    setSibling(siblingFields?.postalCode, suggestion.postalCode);
    setSibling(siblingFields?.city, suggestion.locality);
    setSibling(siblingFields?.country, country);
  };

  return (
    <Autocomplete<AddressSuggestion>
      name={name}
      label={label}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      value={query}
      onValueChange={next => {
        setTypedValue(next);
        onChange(next);
      }}
      onBlur={() => {
        // End this search: no auto-query until the user types here again.
        setTypedValue(null);
        onBlur?.();
      }}
      placeholder={placeholder}
      disabled={disabled}
      items={suggestions}
      isLoading={isFetching}
      renderItem={suggestion => suggestion.label}
      itemKey={(suggestion, index) => `${suggestion.label}-${index}`}
      onSelect={selectSuggestion}
      itemTestId="address-suggestion"
      icon={icon}
    />
  );
};
